import { CommodityType } from '../zentility/_shared/_zen-legacy-common/_models/commodity';

export enum BillStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  REVIEW_NEEDED = 'REVIEW_NEEDED',
  APPROVED = 'APPROVED',
  ERROR = 'ERROR',
  DUPLICATE_DETECTED = 'DUPLICATE_DETECTED'
}

// ============================================================================
// Onboarding Context - for entity-level onboarding sessions
// ============================================================================

/**
 * The level in the portfolio hierarchy where onboarding is occurring
 */
export type OnboardingLevel = 'PORTFOLIO' | 'CUSTOMER' | 'LEN' | 'SERVICE_ADDRESS' | 'METER';

/**
 * Known address structure for context comparison
 */
export interface KnownAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Context passed to onboarding components to indicate what level we're at
 * and what values are already known (for validation/mismatch warnings)
 */
export interface OnboardingContext {
  level: OnboardingLevel;

  // Entity IDs (for linking)
  customerId?: string;
  lenId?: string;
  serviceAddressId?: string;
  meterId?: string;

  // Known values (for mismatch comparison)
  customerName?: string;
  lenName?: string;
  serviceAddress?: KnownAddress;
  utilityAccountNumber?: string;

  // Storage key suffix (e.g., "customer_123" or "serviceAddress_456")
  storageKey: string;
}

/**
 * Mismatch warning when extracted data doesn't match known context
 */
export interface MismatchWarning {
  field: string;
  fieldLabel: string;
  expectedValue: string;
  extractedValue: string;
  severity: 'warning' | 'error';
}

/**
 * Generates a hierarchical storage key for an onboarding context
 * Format: c_{customerId}_le_{lenId}_sa_{serviceAddressId}_m_{meterId}
 * Only includes levels up to and including the target level
 */
export function generateStorageKey(
  level: OnboardingLevel,
  ids: {
    customerId?: string;
    lenId?: string;
    serviceAddressId?: string;
    meterId?: string;
  }
): string {
  if (level === 'PORTFOLIO') {
    return 'portfolio';
  }

  const parts: string[] = [];

  // Always include customer if available
  if (ids.customerId) {
    parts.push(`c_${ids.customerId}`);
  }

  if (level === 'CUSTOMER') {
    if (!ids.customerId) throw new Error('Customer ID required for CUSTOMER level');
    return parts.join('_');
  }

  // Include LEN for LEN level and below
  if (ids.lenId) {
    parts.push(`le_${ids.lenId}`);
  }

  if (level === 'LEN') {
    if (!ids.lenId) throw new Error('LEN ID required for LEN level');
    return parts.join('_');
  }

  // Include service address for SERVICE_ADDRESS level and below
  if (ids.serviceAddressId) {
    parts.push(`sa_${ids.serviceAddressId}`);
  }

  if (level === 'SERVICE_ADDRESS') {
    if (!ids.serviceAddressId) throw new Error('Service Address ID required for SERVICE_ADDRESS level');
    return parts.join('_');
  }

  // Include meter for METER level
  if (ids.meterId) {
    parts.push(`m_${ids.meterId}`);
  }

  if (level === 'METER') {
    if (!ids.meterId) throw new Error('Meter ID required for METER level');
    return parts.join('_');
  }

  return parts.join('_');
}

export type BillType = 'utility' | 'supplier' | 'unknown';

export type ColumnKey =
| 'status'
| 'provider'
| 'utility'
| 'type'
| 'legalEntity' 
| 'utilityAccountNumber'
| 'utilityAccountNumber2'
| 'startDate' 
| 'endDate' 
| 'usage' 
| 'amount' 
| 'confidence' 
| 'actions' 
| 'supplyCharges' 
| 'supplyTaxes' 
| 'distributionCharges' 
| 'distributionTaxes'
| 'fileName'
| 'supplyContractCharges'
| 'supplyEnergyCharges'
| 'supplyCapacityCharges'
| 'supplyTransmissionCharges'
| 'supplyMarketCharges'
| 'supplyLineLossCharges'
| 'supplyGrtSut'
| 'supplyMiscCharges'
| 'supplySalesTaxes'
| 'distributionDemandCharges'
| 'distributionEnergyCharges'
| 'distributionCustomerCharges'
| 'distributionMiscCharges'
| 'distributionTaxCharges'
| 'warnings';

export interface ChargeLineItem {
  description: string;
  rawDescription?: string;      // Raw text from PDF
  amount: number;
  rawAmount?: string;            // Raw text from PDF
  rate?: number;
  rawRate?: string;              // Raw text from PDF
  usage?: number;
  unit?: string;
  rawUnit?: string;              // Raw text from PDF
  confidence?: number;           // Confidence for this line item
}

export interface ChargeGroup {
  total: number;
  rawTotal?: string;             // Raw text from PDF
  confidence?: number;           // Confidence for the total
  lineItems: ChargeLineItem[];
}

// New interface for name value identifiers from API
export interface NameValueIdentifier {
  name: string;                  // EXACT label from bill (e.g., "Acct #", "ESIID")
  value: string;                 // The identifier value
  confidence: number;            // Confidence score (0-1)
}

export interface ParsedBillData {
  /** Commodity this bill's data represents. Dual-fuel PDFs produce one ParsedBillData per commodity. */
  commodityType?: CommodityType;

  // Legal Entity
  legalEntity: string;
  rawLegalEntity?: string;       // Raw text from PDF

  // Service Address
  serviceAddressStreet1: string;
  serviceAddressStreet2: string;
  serviceAddressCity: string;
  serviceAddressState: string;
  serviceAddressZip: string;
  serviceAddressVerified?: boolean;
  rawServiceAddress?: {          // Raw text from PDF
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };

  // Billing Address
  billingAddressName1?: string;  // NEW: Name line 1 on billing address
  billingAddressName2?: string;  // NEW: Name line 2 on billing address
  billingAddressStreet1?: string;
  billingAddressStreet2?: string;
  billingAddressCity?: string;
  billingAddressState?: string;
  billingAddressZip?: string;
  rawBillingAddress?: {          // Raw text from PDF
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };

  // Contract Information
  supplierId?: string;           // Resolved supplier ID from backend matching
  providerName: string;          // Supplier name (or "On Utility")
  rawProviderName?: string;      // Raw text from PDF
  rateType?: 'FIXED' | 'VARIABLE' | 'INDEXED' | null;  // NEW: Contract rate type
  rateTypeConfidence?: number;   // NEW: Confidence for rate type
  contractEndDate?: string;      // NEW: Contract end date (ISO string)
  rawContractEndDate?: string;   // NEW: Raw text from PDF

  // Billing Information
  utilityId?: number;            // Resolved utility ID from backend matching
  utilityName: string;           // Utility company (ComEd, BGE, etc.)
  rawUtilityName?: string;       // Raw text from PDF
  rateClass?: string;            // Utility rate class / tariff text from the bill (e.g. "GS-1"); matched server-side during /match
  utilityAccountNumber: string;  // Primary account number
  rawUtilityAccountNumber?: string; // Raw text from PDF
  utilityAccountNumber2?: string; // Secondary account number (legacy field, prefer additionalIdentifiers)
  utilityAccountNumber3?: string; // Tertiary account number (legacy field, prefer additionalIdentifiers)

  // Canonical utility-specific labels for UAN1/2/3, sourced from the
  // utility_account_number table during backend extraction. Populated when the
  // utility match succeeds; absent when the bill was extracted without a
  // utility or produced by a pre-label-change backend.
  utilityAccountNumberLabel?: string;
  utilityAccountNumber2Label?: string;
  utilityAccountNumber3Label?: string;

  // Additional identifiers (e.g., ESIIDs, secondary account numbers)
  additionalIdentifiers: NameValueIdentifier[];  // CHANGED: Now array of objects instead of Record

  billingPeriodStart: string;    // ISO date string
  billingPeriodEnd: string;      // ISO date string

  totalAmount: number;
  rawTotalAmount?: string;       // Raw text from PDF
  currency: string;              // Default "USD"

  usageAmount: number;           // Total billed usage
  rawUsageAmount?: string;       // Raw text from PDF
  usageUnit: string;             // "kWh", "therms", etc.
  usageUnitId?: number;          // UnitDto.id matching usageUnit — set together with it via the form's handleUnitChange; drives the saved unitId
  financialsDisabled?: boolean;  // Total-only bill (both sections off): save just totalAmount. Derived = supplyDisabled && distributionDisabled.
  supplyDisabled?: boolean;      // Supply section off — not on this bill (e.g. a dual utility bill). Skips supply entry + validation.
  distributionDisabled?: boolean; // Distribution section off — not on this bill (e.g. a dual supplier bill). Skips distribution entry + validation.

  demandKw?: number;             // NEW: Demand in kW
  rawDemandKw?: string;          // NEW: Raw text from PDF

  // Interval usages (TOU breakdown)
  onPeakUsage?: number;
  onPeakCost?: number;
  intermediateUsage?: number;
  intermediateCost?: number;
  offPeakUsage?: number;
  offPeakCost?: number;

  // Supply charges (API calculates these; null = absent on the bill, e.g. a
  // legacy bill that stores only the section total)
  supplyTotalCost: number | null;   // Total supply cost including taxes
  supplyCharges: number | null;     // Supply charges (excluding taxes)
  supplyTaxes: number | null;       // Supply taxes (stateTaxes from API)
  rawSupplyTotalCost?: string;   // Raw text from PDF
  rawSupplyCharges?: string;     // Raw text from PDF

  // Supply charge buckets (populated in budgeting/pro mode)
  supplyContractCharges?: ChargeGroup;
  supplyEnergyCharges?: ChargeGroup;
  supplyCapacityCharges?: ChargeGroup;
  supplyTransmissionCharges?: ChargeGroup;
  supplyMarketCharges?: ChargeGroup;
  supplyLineLossCharges?: ChargeGroup;
  supplyGrtSut?: ChargeGroup;
  supplyMiscCharges?: ChargeGroup;
  supplySalesTaxes?: ChargeGroup;       // State taxes charge bucket (detailed line items)
  // Gas-specific supply charge buckets (budgeting mode); Misc/GRT-SUT/Sales Tax reuse the fields above
  supplyCustomerCharges?: ChargeGroup;
  supplyGasCharges?: ChargeGroup;
  supplyOffPeakCharges?: ChargeGroup;
  supplyRateClassCharges?: ChargeGroup;

  // Distribution charges (API calculates these; null = absent on the bill)
  distributionTotalCost: number | null; // Total distribution cost including taxes
  distributionCharges: number | null;   // Distribution charges (excluding taxes)
  distributionTaxes: number | null;     // Distribution taxes (stateTaxes from API)
  rawDistributionTotalCost?: string; // Raw text from PDF
  rawDistributionCharges?: string;   // Raw text from PDF

  // Distribution charge buckets (populated in budgeting/pro mode)
  distributionCustomerCharges?: ChargeGroup;
  distributionEnergyCharges?: ChargeGroup;
  distributionDemandCharges?: ChargeGroup;
  distributionMiscCharges?: ChargeGroup;
  distributionTaxCharges?: ChargeGroup;  // State taxes charge bucket (detailed line items)
  // Gas-specific distribution charge buckets (budgeting mode); Customer/Misc/Sales Tax reuse the fields above
  distributionGasCharges?: ChargeGroup;
  distributionDeliveryCharges?: ChargeGroup;
  distributionOffPeakCharges?: ChargeGroup;
  distributionBalancingCharges?: ChargeGroup;
  distributionRateClassCharges?: ChargeGroup;
  distributionGrtSut?: ChargeGroup;

  // Metadata
  billType: BillType;            // document type: 'utility' | 'supplier' | 'unknown'
  isDualBilling?: boolean;       // Dual billing method (separate utility + supplier bills). Distinguishes a dual utility bill (billType 'utility', distribution only) from a consolidated one (both sides).
  confidence: number;            // Overall confidence (average of all field confidences)

  // Per-field confidence scores
  fieldConfidence?: {
    legalEntity?: number;
    serviceAddressStreet1?: number;
    serviceAddressStreet2?: number;
    serviceAddressCity?: number;
    serviceAddressState?: number;
    serviceAddressZip?: number;
    billingAddressStreet1?: number;
    billingAddressStreet2?: number;
    billingAddressCity?: number;
    billingAddressState?: number;
    billingAddressZip?: number;
    providerName?: number;
    rateType?: number;
    contractEndDate?: number;
    utilityName?: number;
    utilityAccountNumber?: number;
    totalAmount?: number;
    usageAmount?: number;
    demandKw?: number;
    onPeakUsage?: number;
    onPeakCost?: number;
    intermediateUsage?: number;
    intermediateCost?: number;
    offPeakUsage?: number;
    offPeakCost?: number;
    supplyTotalCost?: number;
    supplyCharges?: number;
    distributionTotalCost?: number;
    distributionCharges?: number;
  };

  // Hierarchical entity matching results (from backend)
  matchedCustomer?: { id: number; name: string; confidence: number };
  matchedLen?: { id: string; name: string; confidence: number };
  matchedServiceAddress?: { id: number; formattedAddress: string; confidence: number };
  matchedMeter?: { id: number; utilityAccountNum1: string; confidence: number };

  // Linking (for future use)
  linkedProviderId?: string;
  linkedLegalEntityId?: string;
  linkedServiceAddressId?: string;
  linkedUtilityAccountId?: string;

  verifiedFields?: string[];

  // Energy Plan
  energyPlanId?: number;
}

export interface BillFile {
  id: string;
  file: File;
  previewUrl: string;
  fileData?: Uint8Array;
  status: BillStatus;
  data: ParsedBillData | null;
  error?: string;
  duplicateOfId?: string;
  /** Commodity this row represents — a dual-fuel PDF yields one BillFile per commodity */
  commodityType?: CommodityType;
  /** For the GAS row split out of a dual-fuel PDF: the id of the primary (ELECTRICITY) BillFile */
  dualFuelOf?: string;
  commodityTypes?: CommodityType[];
  warnings?: string[];
  energyPlanId?: number;
  /** Raw API extraction data — preserved for merge API and AI training */
  rawExtraction?: import('./services/api-models').AiBillExtraction;
  /** When this bill has been merged into another bill (dual billing), stores the target bill's ID */
  mergedInto?: string;
  /** Server-side temporary file ID — used to fetch the PDF when local storage (IndexedDB) fails */
  tempFileId?: string;
  /** Id of the AI request log row of this commodity's extraction — sent as billAiId on import */
  billAiId?: number;
  /** Position of this row's billing period when the PDF prints several for the commodity (1-based) */
  periodIndex?: number;
  /** How many billing periods the PDF prints for this row's commodity; undefined on a one-period bill */
  periodCount?: number;
}

export interface CustomerProfile {
    companyName: string;
    billingAddressStreet1: string;
    billingAddressStreet2: string;
    billingAddressCity: string;
    billingAddressState: string;
    billingAddressZip: string;
    contactFirstName: string;
    contactLastName: string;
    contactEmail: string;
    contactPhone: string;
    isApproved: boolean;
}

export interface MatchResult {
    id: string;
    label: string;
    subLabel?: string;
    score: number;
    type: 'entity' | 'address' | 'account' | 'provider';
}

// Bill Grouping - pairs utility and supplier bills for the same service period
export type BillGroupMatchConfidence = 'high' | 'medium' | 'low' | 'unpaired';

export interface BillGroup {
    id: string;
    utilityBill: BillFile | null;
    supplierBill: BillFile | null;
    matchConfidence: BillGroupMatchConfidence;
    matchReason?: string;
    /** True when the user explicitly unpaired these bills; enables re-pairing. */
    rejected?: boolean;
    billingPeriod: {
        start: string;
        end: string;
    };
    combinedAmount: number;
    combinedUsage: number;
    usageUnit: string;
}

// Re-export bill import models
export * from './models/bill-import.models';
